import type { BootData } from '@/types/boot';
import type { AvailablePaymentMethod, CheckoutData, Customer, PaymentMethod } from '@/types/checkout';
import type { AdyenApplePayPaymentMethod } from '@/types/adyen';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { isMobileDevice } from './device';
import { deferIdle } from './defer';
import { isEmbeddedTemplateType } from './template';
import { getRequestedTemplateType } from './templateInitializer';

/**
 * Append a `<link rel="preload" as="image" fetchpriority="high">` for
 * the boot's background image. The browser starts the image fetch in
 * parallel with the React tree build, so the background is in the
 * cache by the time the first paint happens.
 *
 * No-op when the boot has no theme or no resolved URL — the call site
 * doesn't need to guard.
 */
export const preloadBackgroundImage = (boot: BootData | undefined | null): void => {
  const theme = boot?.checkoutTheme;
  if (!theme) return;
  const bgUrl = isMobileDevice() ? theme.mobileBgImage || theme.backgroundImage : theme.backgroundImage;
  if (!bgUrl) return;

  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = bgUrl;
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
};

/** Publisher text face for EMBEDDED_VIEW, served from the publisher CDN. */
const EMBEDDED_VIEW_FONT_STYLESHEET = 'https://cdn.supercell.com/fonts/supercell/fonts_supercelltext_full.css';

/**
 * Append the publisher font stylesheet when boot asks for EMBEDDED_VIEW.
 * The family is applied by `.checkout-embedded-view-font` on that view's
 * container, so every other checkout keeps the default stack and never pays for
 * this third-party request.
 *
 * Injected here — before React mounts, off the boot decode — rather than from an
 * effect in the view, so the face is in flight during boot and the checkout's
 * first paint is already in the right font instead of reflowing out of the
 * fallback. Gated on the REQUESTED template (see `getRequestedTemplateType`):
 * waiting for eligibility to resolve would need `/checkout/v3`, which lands well
 * after this point, and the worst case of the looser gate is one unused
 * stylesheet on the desktop fallback to DEFAULT.
 */
export const preloadEmbeddedViewFont = (boot: BootData | undefined | null): void => {
  if (!isEmbeddedTemplateType(getRequestedTemplateType(boot))) return;
  if (document.querySelector(`link[href="${EMBEDDED_VIEW_FONT_STYLESHEET}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = EMBEDDED_VIEW_FONT_STYLESHEET;
  document.head.appendChild(link);
};

/**
 * Idle-prewarm every PSP SDK chunk we might need (Adyen, Stripe,
 * Braintree/Venmo, PayPal) + the FontAwesome CSS chunk. No SDK
 * initialization happens here — only the chunk download + parse, in
 * parallel with first paint. By the time a wallet hook reaches
 * `import('@adyen/…')` the module is in the V8 cache so the dynamic
 * import resolves synchronously.
 *
 * `braintree-web` is included because Venmo (via `useVenmoBraintree`)
 * is the only path that pulls it in, and Venmo's CTA tends to be on the
 * critical path on the iOS/Safari fallback when Apple Pay is configured
 * but unavailable. The chunk is tree-shakable but ~50KB even minified,
 * so warming at idle saves a real RTT on first Venmo click.
 *
 * Stripe's runtime script (`js.stripe.com/clover/stripe.js`) is loaded
 * separately from `index.html` so it lands during HTML parse, before this
 * idle callback even fires; the `/pure` entry warmed here adopts that tag
 * instead of injecting a second one.
 */
export const prewarmPspChunks = (): void => {
  deferIdle(() => {
    void import('../components/Common/FontAwesomeIcon/style.css');
    void import('@adyen/adyen-web');
    void import('@stripe/stripe-js/pure');
    void import('@paypal/react-paypal-js');
    void import('braintree-web');
  });
};

/**
 * Preload the FontAwesome icon webfonts on the critical path.
 *
 * The `@font-face` blocks use `font-display: block`, so icon-bearing UI is
 * held invisible (rather than showing a `.notdef` tofu square) until the
 * font is available. Kicking the woff2 fetch off here — in parallel with
 * the bundle + `/checkout/v3` — shortens that invisible window so icons pop
 * in sooner instead of after the idle-deferred CSS chunk starts the fetch.
 *
 * Only `solid` (900, e.g. every close `fa-xmark`) and `regular` (400, back
 * arrows / trash / chevrons / cart) are warmed — they cover the icons on the
 * primary screens. `light` (300) backs only a few field-adornment icons, so
 * it's intentionally left to load via the CSS chunk to avoid spending another
 * ~366KB on the critical path.
 *
 * URLs are resolved via `new URL(..., import.meta.url)` so they track Vite's
 * content-hashed asset filenames instead of a brittle static path. `crossorigin`
 * is required on font preloads (even same-origin) for the fetch to match the
 * CSS-driven request and be reused from cache.
 */
export const preloadFontAwesomeFonts = (): void => {
  const fontUrls = [
    new URL('../components/Common/FontAwesomeIcon/fa-solid-900.woff2', import.meta.url).href,
    new URL('../components/Common/FontAwesomeIcon/fa-regular-400.woff2', import.meta.url).href,
  ];

  for (const href of fontUrls) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
};

/**
 * FTD / last-method (PREFERRED_PAYMENT_METHOD) prewarm: when the user
 * is on a single-CTA template that can fall back to DEFAULT, eagerly
 * (but only at idle) initialize the PSP instances DEFAULT will need.
 *
 * What this saves on fallback:
 *   - Adyen card form: `adyenService.initialize(config, customer)`
 *     ends up calling `await AdyenCheckout(config)` which is the
 *     ~100-400ms hot path. The service is a singleton, so a later
 *     `useAdyen` mount short-circuits to the cached instance.
 *   - Stripe sub-mid pks: each non-express Stripe `pspSubMidPk` gets a
 *     `loadStripe(pk)` kick-off. `loadStripe()` is memoized by pk —
 *     the later `useStripeInitialize({isExpress:false})` resolves on
 *     the next microtask. (`<script async>` already made Stripe.js
 *     itself instant, this also warms the merchant config round trip.)
 *
 * What this deliberately skips:
 *   - PayPal SDK (`https://www.paypal.com/sdk/js`) — 150KB+ script
 *     that injects globals + event listeners; not worth the cost when
 *     the user may never fall back.
 *   - Braintree / Nuvei — same reasoning, additive risk > the rare
 *     fallback win.
 *
 * Strictly idempotent and idle-scheduled. Safe to call multiple times.
 */
export const prewarmFallbackTemplatePayments = ({
  paymentMethods,
  customer,
}: {
  paymentMethods: AvailablePaymentMethod[] | PaymentMethod[] | undefined;
  customer: Customer | undefined;
}): void => {
  if (!paymentMethods || paymentMethods.length === 0) return;

  // Snapshot the methods that we will warm, so an idle-time
  // `paymentMethods` mutation can't change what we iterate.
  const stripePks = new Set<string>();
  let adyenCardConfig: { config: Record<string, unknown>; raw: PaymentMethod } | null = null;
  let adyenApplePayMethod: AvailablePaymentMethod | PaymentMethod | null = null;

  for (const pm of paymentMethods) {
    if (pm.provider === ProviderEnum.STRIPE && pm.pspSubMidPk) {
      stripePks.add(pm.pspSubMidPk);
    }
    if (
      pm.provider === ProviderEnum.ADYEN &&
      pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD &&
      pm.publicConfig
    ) {
      adyenCardConfig = { config: pm.publicConfig as Record<string, unknown>, raw: pm };
    }
    if (pm.provider === ProviderEnum.ADYEN && pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY && pm.publicConfig) {
      adyenApplePayMethod = pm;
    }
  }

  if (stripePks.size === 0 && !adyenCardConfig && !adyenApplePayMethod) return;

  deferIdle(() => {
    for (const pk of stripePks) {
      void warmStripePk(pk);
    }
    if (adyenCardConfig) {
      void warmAdyenCard(adyenCardConfig.config, customer);
    }
    if (adyenApplePayMethod) {
      void warmAdyenApplePay(adyenApplePayMethod, customer);
    }
  });
};

/**
 * Memoized loader for the Stripe runtime + config. The first
 * `warmStripePk` call pays the dynamic-import cost; every subsequent
 * call (same tick or later) awaits the SAME resolved promise — so a
 * burst of N parallel warms costs one import resolution, not N. The
 * underlying `@stripe/stripe-js` chunk is already idle-prewarmed by
 * `prewarmPspChunks` and HTML-preloaded by `<script async>`, so this
 * usually resolves from the V8 module cache on the very first call.
 */
let stripeLoaderPromise: Promise<{
  loadStripe: typeof import('@stripe/stripe-js/pure').loadStripe;
  STRIPE_CONFIG: typeof import('@/constants/stripe').STRIPE_CONFIG;
}> | null = null;
const getStripeLoader = () => {
  if (!stripeLoaderPromise) {
    stripeLoaderPromise = Promise.all([import('@stripe/stripe-js/pure'), import('@/constants/stripe')]).then(
      ([stripe, config]) => ({ loadStripe: stripe.loadStripe, STRIPE_CONFIG: config.STRIPE_CONFIG })
    );
  }
  return stripeLoaderPromise;
};

const warmStripePk = async (pk: string): Promise<void> => {
  try {
    const { loadStripe, STRIPE_CONFIG } = await getStripeLoader();
    await loadStripe(pk, STRIPE_CONFIG);
  } catch {
    // non-fatal: real init paths will retry
  }
};

/**
 * BIG-WIN prewarm driven off the `/checkout/v3` prefetch (kicked off in
 * `main.tsx` via `kickoffCheckoutDataPrefetch` at module-eval time, in
 * parallel with React mount + boot decode).
 *
 * Why this isn't `pks.stripe` / `pks.adyen` directly:
 *   - Each Stripe payment method has its OWN `pspSubMidPk` and that's
 *     what `useStripeInitialize` → `getStripePaymentKeyBySelectedPaymentMethod`
 *     hands to `loadStripe()`. The top-level `pks.stripe` is only a
 *     defensive fallback when no method matches by name. Warming the
 *     wrong pk would be a no-op (loadStripe memoizes per pk) — wasted
 *     prewarm rather than a hot-cache hit.
 *   - Adyen card init is a singleton (`adyenService.initialize` —
 *     first call wins). The real config used by `useAdyen` is the
 *     per-method `publicConfig` (`getAdyenConfig`), which carries env +
 *     clientKey + paymentMethodsResponse. A synthesized config built
 *     from `pks.adyen` would POISON the singleton — `useAdyen`'s later
 *     init would short-circuit to the prewarmed (wrong-shape) instance.
 *
 * So this fn does the SAME pk routing the real init paths do:
 *   - Iterate `data.paymentMethods`, collect every Stripe
 *     `pspSubMidPk`, fire `loadStripe(pk)` per unique pk (critical
 *     path, NOT idle-deferred — this is the win we're after).
 *   - Find the Adyen card method's `publicConfig`, idle-deferred init
 *     via `adyenService.initialize(publicConfig, customer)`. Identical
 *     args to what `useAdyen.initializeAdyen` passes, so the singleton
 *     short-circuits cleanly when the real form mounts.
 *
 * Dedup is via `loadStripe()`'s upstream pk-keyed memoization +
 * `adyenService.initialize`'s singleton guard. We also Set-dedupe
 * Stripe pks here to avoid issuing N identical loadStripe calls when
 * multiple methods share a pk (the common case).
 */
export const prewarmPspFromCheckoutData = (data: CheckoutData | undefined | null): void => {
  const methods = data?.paymentMethods;
  if (!methods || methods.length === 0) return;

  const stripePks = new Set<string>();
  let adyenCardConfig: Record<string, unknown> | null = null;
  let adyenApplePayMethod: PaymentMethod | null = null;

  for (const pm of methods) {
    if (pm.provider === ProviderEnum.STRIPE && pm.pspSubMidPk) {
      stripePks.add(pm.pspSubMidPk);
    }
    if (
      pm.provider === ProviderEnum.ADYEN &&
      pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD &&
      pm.publicConfig
    ) {
      adyenCardConfig = pm.publicConfig as Record<string, unknown>;
    }
    if (pm.provider === ProviderEnum.ADYEN && pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY && pm.publicConfig) {
      adyenApplePayMethod = pm;
    }
  }

  // Stripe pks fire immediately — `loadStripe()` is fully parallel
  // with React mount and the BE response landing here is already the
  // gating event for the actual init path.
  for (const pk of stripePks) {
    void warmStripePk(pk);
  }

  // Adyen is idle-deferred — `AdyenCheckout()` is heavier (~100-400ms)
  // and the form/wallet isn't always reached. The card + Apple Pay
  // checkout instances are separate singletons (`adyenService` vs
  // `adyenApplePayService`), so warming both is safe and a later real
  // init short-circuits to whichever cached instance it owns.
  if (adyenCardConfig || adyenApplePayMethod) {
    const customer = data?.customer;
    deferIdle(() => {
      if (adyenCardConfig) {
        void warmAdyenCard(adyenCardConfig, customer);
      }
      if (adyenApplePayMethod) {
        void warmAdyenApplePay(adyenApplePayMethod, customer);
      }
    });
  }
};

const warmAdyenCard = async (publicConfig: Record<string, unknown>, customer: Customer | undefined): Promise<void> => {
  try {
    // Dynamic import so this prewarm never pulls Adyen into a sync
    // boot path. The `@adyen/adyen-web` chunk is already idle-warmed
    // by `prewarmPspChunks`, so this resolves from cache.
    const { adyenService } = await import('../services/adyenService');
    // adyenService.initialize is idempotent — if the user-triggered
    // card form mount somehow races us, the later call short-circuits
    // to the same cached instance.
    await adyenService.initialize(publicConfig as never, customer);
  } catch {
    // non-fatal: real init paths will retry
  }
};

/**
 * Warm the Adyen Apple Pay `AdyenCheckout()` instance ahead of first mount.
 *
 * Adyen Apple Pay lives on its OWN singleton (`adyenApplePayService`), distinct
 * from the card singleton warmed by `warmAdyenCard`. Without this, the first
 * `useAdyenApplePay` mount pays the full cold cost — dynamic import +
 * `AdyenCheckout()` init (translations/config CDN round trip) + component
 * construction — while the express row shows a spinner. `initializeCheckoutInstance`
 * is guarded by a singleton + `isInitializing` flag, so a later real mount
 * short-circuits to this cached instance (or awaits the in-flight init).
 *
 * The whole payment method (not just `publicConfig`) is required because
 * `createAdyenApplePayConfiguration` also reads `paymentMethodConfig`.
 */
const warmAdyenApplePay = async (
  applePayMethod: AvailablePaymentMethod | PaymentMethod,
  customer: Customer | undefined
): Promise<void> => {
  try {
    const { adyenApplePayService } = await import('../services/adyenApplePayService');
    await adyenApplePayService.initializeCheckoutInstance(
      applePayMethod as unknown as AdyenApplePayPaymentMethod,
      customer
    );
  } catch {
    // non-fatal: the real `useAdyenApplePay` init path will retry
  }
};
